import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../api/client';
import { meetingDetail } from '@contracts/meetings';
import type { SeriesSettingsBody } from '@contracts/series';
import {
  useMeetings,
  useNames,
  useOpen,
  useSeriesList,
  useSeriesSettings,
  useSettings,
  useSlack,
  useSlackChannels,
  useAsana,
  useAsanaProjects,
  useAsanaWorkspaces,
  useChannels,
  useChannelActions,
  useUpdateSeries,
} from '../api/queries';
import {
  BackLink,
  Balances,
  Broken,
  Button,
  Card,
  Empty,
  Field,
  Grade,
  Icon,
  ListSwitch,
  MenuButton,
  Modal,
  Rows,
  SearchField,
  Picker,
  SkeletonRows,
  Switch,
  Who,
  useNotices,
} from '../ui';
import { ago, between, clock, dateLabel, duration, since } from '../lib/time';
import { guessName } from '../lib/note';
import { Namesakes, SameSeriesAs } from '../series/JoinSeries';
import { SeriesWriteups } from '../series/SeriesWriteups';
import { DefaultTemplate, SendToSlack } from '../series/SeriesNote';
import { seriesWhen } from '../series/when';

const WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * Board 18 — a series, its settings, and what it has produced. The series
 * decides the shape of every note it produces, so its settings are a table
 * of live controls: each one takes effect for every instance from now on the
 * moment it is changed. Nothing here waits for a Save button.
 */
export function Series(): ReactElement {
  const [adding, setAdding] = useState(false);
  const { key = '' } = useParams();
  const settings = useSeriesSettings(key || null);
  const mine = useSettings();
  const list = useSeriesList();
  const instances = useMeetings({ series: key });
  const open = useOpen({ series: key });
  const channels = useChannels();
  const channelActions = useChannelActions();
  const following = (channels.data?.mine ?? []).filter((c) => c.series.includes(key));
  const notFollowing = (channels.data?.mine ?? []).filter((c) => !c.series.includes(key));
  const names = useNames();
  const update = useUpdateSeries();
  const slack = useSlack();
  const slackChannels = useSlackChannels(slack.data?.connected === true);
  const asana = useAsana();
  const asanaWorkspaces = useAsanaWorkspaces(asana.data?.connected === true);
  // Picked here, before any project is — the series may not have one yet.
  const [pickedWorkspace, setPickedWorkspace] = useState<string | null>(null);
  // ⚠ Above the early returns below — a hook runs on every render or none.
  // Read off `settings.data` rather than `s`, which is not declared yet.
  // The workspace this series' project lives in: the one picked just now, the
  // one already chosen for it, wherever this person last sent a task — and
  // failing all three, their FIRST workspace. It stopped at the third until
  // 2026-09-23, so somebody who had connected Asana and never sent a task yet
  // had no workspace, no projects, and a menu reading only "Asked each time"
  // (Vladimir, on Alpha Daily) — the same fallback the Send dialog always had.
  const asanaWorkspace =
    pickedWorkspace ??
    settings.data?.asanaWorkspaceGid ??
    asana.data?.defaultWorkspaceGid ??
    asanaWorkspaces.data?.[0]?.gid ??
    null;
  const asanaProjects = useAsanaProjects(asana.data?.connected ? asanaWorkspace : null);
  const { show } = useNotices();
  const summary = (list.data ?? []).find((s) => s.seriesKey === key) ?? null;
  const cadence = useMemo(
    () => cadenceOf((instances.data ?? []).map((m) => m.startedAt)),
    [instances.data],
  );

  const [botName, setBotName] = useState('');
  useEffect(() => setBotName(settings.data?.botDisplayName ?? ''), [settings.data?.botDisplayName]);

  if (settings.isPending)
    return (
      <div className="page">
        <SkeletonRows rows={4} />
      </div>
    );
  if (settings.isError) {
    const notYours = settings.error instanceof ApiError && settings.error.status === 404;
    return (
      <div className="page">
        <Card flush>
          {notYours ? (
            <Empty
              lead="You are not in this series."
              action={
                <Link to="/people" className="btn">
                  People and series
                </Link>
              }
            >
              A recurring meeting belongs to the people who meet in it. If you are meant to be
              there, the invitation puts you in.
            </Empty>
          ) : (
            <Broken what="This series" retry={() => void settings.refetch()} />
          )}
        </Card>
      </div>
    );
  }
  const s = settings.data;
  // Its own name, the newest meeting's, or the calendar's — never the key,
  // which a series not recorded yet would otherwise show (PRD 65).
  const title = s.title;
  const save = (
    body: SeriesSettingsBody,
    text = 'Saved — every instance from now on runs this way.',
  ) =>
    update.mutate(
      { key, body },
      {
        onSuccess: () => show({ tone: 'success', text }),
        onError: (e) => show({ tone: 'failure', text: e.message }),
      },
    );
  const saveName = () => {
    const next = botName.trim() || null;
    if (next !== s.botDisplayName) save({ botDisplayName: next });
  };
  const items = (open.data?.items ?? []).filter((i) => i.direction !== 'SOMEONE_ELSE');

  return (
    <div className="page stack-lg">
      {/* ⚠ Opening one series is still being in Series, and the switch is how
          somebody gets back to the other list. Losing it here was read as the
          switch disappearing (Vladimir, 2026-09-21: "when I select series in
          meeting tab the meeting tab select on top is removed"). */}
      <ListSwitch on="series" />
      <div className="toolbar">
        <BackLink to="/series">Series</BackLink>
        <span className="crumb">{title}</span>
        <div className="actions">
          <Button size="sm" onClick={() => setAdding(true)}>
            Add meetings…
          </Button>
          {summary ? <SameSeriesAs here={summary} list={list.data ?? []} /> : null}
          <Link to={`/ask?scope=series&scopeId=${encodeURIComponent(key)}`} className="btn btn-sm">
            Ask this series
          </Link>
        </div>
      </div>
      <div className="between" style={{ alignItems: 'flex-start' }}>
        <div className="cluster" style={{ gap: 14, flexWrap: 'nowrap' }}>
          <span className="avatar room xl">
            <Icon name="space" size={24} />
          </span>
          <div className="stack" style={{ gap: 2 }}>
            <h1 className="t-title">{title}</h1>
            {summary?.lastMetAt ? (
              <p className="mono">
                {cadence ? `${cadence} · ` : ''}
                {summary.instances} {summary.instances === 1 ? 'instance' : 'instances'} ·{' '}
                {duration(summary.totalSeconds)} · last met {since(summary.lastMetAt)}
              </p>
            ) : (
              <p className="mono">{summary ? seriesWhen(summary) : 'no recordings yet'}</p>
            )}
          </div>
        </div>
        {summary ? (
          <Balances
            iOwe={summary.open.youOwe}
            theyOwe={summary.open.owedToYou}
            to={`/open?series=${encodeURIComponent(key)}`}
          />
        ) : null}
      </div>

      {summary ? <Namesakes here={summary} list={list.data ?? []} /> : null}

      {adding ? (
        <AddMeetings seriesKey={key} title={title} onClose={() => setAdding(false)} />
      ) : null}

      <div className="grid-2 aside">
        {/* ⚠ Three groups, not one list. Vladimir, 2026-10-08: "make this
            settings more structured as now its just weird list of different
            settgins of series and its hard to navigate". How the notetaker
            comes, what the note is, and where it goes — each a card of its own. */}
        <div className="stack-lg">
          <Card
            title="Recording"
            aside={<span className="t-secondary">how the notetaker comes to every meeting</span>}
            flush
          >
            <div className="setting">
              <span className="k">Record this series</span>
              <div className="v">
                <Switch
                  label=""
                  checked={s.botEnabled}
                  onChange={(e) =>
                    save(
                      { botEnabled: e.target.checked },
                      e.target.checked
                        ? `“${title}” will be recorded from now on.`
                        : `The notetaker stays out of “${title}” from now on — every instance.`,
                    )
                  }
                />
                <span className="why">
                  {!s.botEnabled && mine.data?.recordEverything === false
                    ? 'you record only the meetings you turn on — switch it on for every instance'
                    : 'every instance, unless someone turns one off'}
                </span>
              </div>
            </div>
            <div className="setting">
              <span className="k">Notetaker’s name</span>
              <div className="v">
                <Field
                  aria-label="Notetaker’s name in this series"
                  placeholder="Your own notetaker’s name"
                  value={botName}
                  onChange={(e) => setBotName(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                  style={{ width: 240 }}
                />
                <span className="why">what the other people see join the call</span>
              </div>
            </div>
            <div className="setting">
              <span className="k">Transcription language</span>
              <div className="v">
                <Picker
                  aria-label="Transcription language"
                  value={s.language}
                  onChange={(language) => save({ language })}
                  width={200}
                  options={[
                    { value: 'auto', label: 'Your own default' },
                    { value: 'multi', label: 'Whatever is spoken' },
                    { value: 'en', label: 'English' },
                    { value: 'ru', label: 'Russian' },
                    { value: 'de', label: 'German' },
                    { value: 'fr', label: 'French' },
                    { value: 'es', label: 'Spanish' },
                    { value: 'pl', label: 'Polish' },
                  ]}
                />
              </div>
            </div>
            <div className="setting">
              <span className="k">Keep the video</span>
              <div className="v">
                <Switch
                  label=""
                  checked={s.saveVideo}
                  onChange={(e) => save({ saveVideo: e.target.checked })}
                />
                <span className="why">
                  the words are kept for good; the video for your workspace’s retention period
                </span>
              </div>
            </div>
          </Card>
          <Card
            title="Notes"
            aside={<span className="t-secondary">what every meeting is written up as</span>}
            flush
          >
            <DefaultTemplate s={s} save={save} />
            {/* After every meeting, written up with more templates and posted where
                each rule says (PRD 61) — Aleksandr's ask at the AI Talks. */}
            <div className="setting">
              <span className="k">More write-ups</span>
              <div className="v">
                <SeriesWriteups s={s} save={save} />
              </div>
            </div>
            {/* ⚠ What it is WRITTEN in, which is a different question from what
                it is spoken in (PRD 48). "Your own default" here means this
                series has said nothing and the Settings choice applies.
                Knowledge is deliberately absent: it is read across the whole
                workspace and set once, in Settings (Vladimir, 2026-09-21). */}
            {(
              [
                ['noteLanguage', 'Notes written in'],
                ['ledgerLanguage', 'Commitments written in'],
              ] as const
            ).map(([field, label]) => (
              <div className="setting" key={field}>
                <span className="k">{label}</span>
                <div className="v">
                  <Picker
                    aria-label={label}
                    value={s[field]}
                    onChange={(code) => save({ [field]: code })}
                    width={200}
                    options={[
                      { value: 'auto', label: 'Your own default' },
                      { value: 'en', label: 'English' },
                      { value: 'ru', label: 'Russian' },
                      { value: 'de', label: 'German' },
                      { value: 'fr', label: 'French' },
                      { value: 'es', label: 'Spanish' },
                      { value: 'pl', label: 'Polish' },
                      { value: 'uk', label: 'Ukrainian' },
                      { value: 'cs', label: 'Czech' },
                    ]}
                  />
                </div>
              </div>
            ))}
          </Card>
          <Card
            title="Sharing"
            aside={<span className="t-secondary">where every meeting goes</span>}
            flush
          >
            {/* ⚠ The channel belongs to the SERIES, so the weekly standup always
                lands in the same place with nobody choosing (PRD 33 §4). A
                one-off meeting is asked at the moment it posts. */}
            <div className="setting">
              <span className="k">Slack channel</span>
              <div className="v">
                {/* ⚠ A menu, not a select: it filters as soon as there are more
                    than a handful, which is every real workspace (Vladimir,
                    2026-09-21: "it needs a search as i have many channels"). */}
                <MenuButton
                  label={
                    !slack.data?.connected
                      ? 'Slack is not connected'
                      : s.slackChannelName
                        ? `# ${s.slackChannelName}`
                        : 'Asked each time'
                  }
                  size="sm"
                  items={
                    slack.data?.connected
                      ? [
                          {
                            label: 'Asked each time',
                            current: !s.slackChannelId,
                            onSelect: () =>
                              save(
                                { slackChannelId: null, slackChannelName: null },
                                'No channel for this one — it asks each time.',
                              ),
                          },
                          ...(slackChannels.data ?? []).map((c) => ({
                            label: `${c.isPrivate ? '🔒' : '#'} ${c.name}`,
                            current: c.id === s.slackChannelId,
                            onSelect: () =>
                              save(
                                { slackChannelId: c.id, slackChannelName: c.name },
                                `This series posts to #${c.name}.`,
                              ),
                          })),
                        ]
                      : []
                  }
                />
                <span className="why">
                  public channels work as they are; a private one shows once Ptitim is in it — type
                  /invite @Ptitim there
                </span>
              </div>
            </div>
            <SendToSlack s={s} save={save} />
            {/* ⚠ Channels, not "files into" a space (PRD 58): every meeting of
                the series is POSTED to the channel — shared with everybody in it
                — as it happens. */}
            <div className="setting">
              <span className="k">Collections</span>
              <div className="v">
                <span className="cluster" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {following.map((c) => (
                    <Link key={c.id} to={`/ch/${c.id}`} className="chip">
                      {c.name}
                    </Link>
                  ))}
                  {notFollowing.length > 0 ? (
                    <MenuButton
                      label={following.length ? '+ Another collection' : 'Add every meeting to…'}
                      size="sm"
                      align="left"
                      items={notFollowing.map((c) => ({
                        id: c.id,
                        label: c.name,
                        onSelect: () =>
                          channelActions.follow.mutate(
                            { id: c.id, seriesKey: key },
                            {
                              onSuccess: ({ posted }) =>
                                show({
                                  tone: 'success',
                                  text: `Every meeting of “${title}” is added to ${c.name} now${posted ? ` — ${posted} already` : ''}.`,
                                }),
                              onError: (err) => show({ tone: 'failure', text: err.message }),
                            },
                          ),
                      }))}
                    />
                  ) : null}
                  {(channels.data?.mine.length ?? 0) === 0 ? (
                    <Link to="/channels?new=1" className="mono">
                      + New collection
                    </Link>
                  ) : null}
                </span>
                <span className="why">
                  {following.length
                    ? 'everybody in these collections can open every meeting of it'
                    : 'in none yet — only the people in each meeting have it'}
                </span>
              </div>
            </div>
            {/* ⚠ A default the Asana picker arrives on — never a silent
                destination. His rule, and the same one the channel above
                follows: "always ask same logic as with slack channel, but have a
                settings in series for default value" (PRD 51). */}
            <div className="setting">
              <span className="k">Tasks go to</span>
              <div className="v">
                {/* Only when there is a choice: one workspace is simply the one. */}
                {asana.data?.connected && (asanaWorkspaces.data?.length ?? 0) > 1 ? (
                  <Picker
                    aria-label="Asana workspace"
                    value={asanaWorkspace ?? ''}
                    onChange={(gid) => setPickedWorkspace(gid || null)}
                    width={170}
                    options={(asanaWorkspaces.data ?? []).map((w) => ({
                      value: w.gid,
                      label: w.name,
                    }))}
                  />
                ) : null}
                <MenuButton
                  label={
                    !asana.data?.connected
                      ? 'Asana is not connected'
                      : ((asanaProjects.data ?? []).find((p) => p.gid === s.asanaProjectGid)
                          ?.name ?? 'Asked each time')
                  }
                  size="sm"
                  items={
                    asana.data?.connected
                      ? [
                          {
                            label: 'Asked each time',
                            current: !s.asanaProjectGid,
                            onSelect: () =>
                              save(
                                { asanaWorkspaceGid: null, asanaProjectGid: null },
                                'No project for this one — the picker asks each time.',
                              ),
                          },
                          ...(asanaProjects.data ?? []).map((p) => ({
                            id: p.gid,
                            label: p.name,
                            current: p.gid === s.asanaProjectGid,
                            onSelect: () =>
                              save(
                                {
                                  asanaWorkspaceGid: asanaWorkspace,
                                  asanaProjectGid: p.gid,
                                },
                                `This series' tasks start in ${p.name}.`,
                              ),
                          })),
                        ]
                      : []
                  }
                />
                <span className="why">the picker still opens — this is where it starts</span>
              </div>
            </div>
          </Card>
        </div>
        <div className="stack-lg">
          {summary?.regulars.length ? (
            <Card
              title="Regulars"
              aside={<span className="t-secondary">who is usually there</span>}
            >
              <div className="cluster" style={{ gap: 6, flexWrap: 'wrap' }}>
                {summary.regulars.map((email) => (
                  <Who
                    key={email}
                    email={email}
                    name={names.get(email.toLowerCase()) ?? guessName(email)}
                    short
                    to={`/people/${encodeURIComponent(email)}`}
                  />
                ))}
              </div>
            </Card>
          ) : null}
          <Card
            title="Rolled forward from last time"
            aside={
              <span className="t-secondary">the app closes these by listening, not by nagging</span>
            }
            flush
          >
            {open.isPending ? (
              <SkeletonRows rows={2} dense />
            ) : items.length === 0 ? (
              <Empty tone="ok" lead="Nothing open from this series." />
            ) : (
              <Rows>
                {items.map((i) => (
                  <div key={i.id} className="row dense">
                    <div className="row-main">
                      <span className="row-title">
                        {i.direction === 'OWED_TO_ME' && i.ownerEmail
                          ? `${(i.ownerName ?? names.get(i.ownerEmail.toLowerCase()) ?? guessName(i.ownerEmail)).split(' ')[0]}: `
                          : ''}
                        {i.text}
                      </span>
                    </div>
                    <span
                      className={`mono ${Date.now() - Date.parse(i.createdAt) > WEEK ? 'amber' : ''}`}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {ago(i.createdAt)}
                    </span>
                    <Grade value={i.grade} />
                  </div>
                ))}
              </Rows>
            )}
          </Card>
          <Card
            title="Instances"
            flush
            aside={instances.data ? <span className="mono">{instances.data.length}</span> : null}
          >
            {instances.isPending ? (
              <SkeletonRows rows={3} />
            ) : instances.isError ? (
              <Broken what="The instances" retry={() => void instances.refetch()} />
            ) : instances.data.length === 0 ? (
              <Empty lead="No recordings yet — the first one follows these settings." />
            ) : (
              <Rows>
                {instances.data.map((m) => (
                  <Link key={m.id} to={`/m/${m.id}`} className="row">
                    <span className="mono" style={{ width: 96 }}>
                      {dateLabel(m.startedAt)} {clock(m.startedAt)}
                    </span>
                    <div className="row-main">
                      <span className="row-title">{m.title}</span>
                      <span className="row-sub">
                        {m.commitmentCount} commitments · {m.decisionCount} decisions
                      </span>
                    </div>
                    <span className="mono">{duration(between(m.startedAt, m.endedAt))}</span>
                  </Link>
                ))}
              </Rows>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * Putting several meetings into a series at once.
 *
 * ⚠ The alternative is opening each meeting and using its own series control,
 * which is two clicks per meeting — fine for the second instance of something,
 * hopeless for assembling a series out of a year of one-offs. Vladimir,
 * 2026-09-21: "What will be with this select when I will visit 1000 on offs
 * meetings?"
 *
 * Deliberately NOT a checkbox column on the Meetings list: a row there is a
 * link you click to read the meeting, and putting a checkbox inside it makes
 * every row two targets that do different things. This is a place you go to
 * assemble, and the list it shows is already filtered to the only meetings
 * that make sense — the ones not already here.
 */
function AddMeetings({
  seriesKey,
  title,
  onClose,
}: {
  seriesKey: string;
  title: string;
  onClose: () => void;
}): ReactElement {
  const all = useMeetings();
  const [needle, setNeedle] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const { show } = useNotices();
  const client = useQueryClient();

  const q = needle.trim().toLowerCase();
  const candidates = (all.data ?? [])
    .filter((m) => m.seriesKey !== seriesKey)
    .filter((m) => !q || m.title.toLowerCase().includes(q));

  const toggle = (id: string) =>
    setPicked((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const add = async () => {
    setSaving(true);
    let done = 0;
    try {
      // One at a time, on purpose: each is its own PATCH, and a failure in the
      // middle leaves the ones before it attached rather than rolling back
      // work somebody watched happen.
      for (const id of picked) {
        await api(meetingDetail, `/v1/meetings/${id}`, { method: 'PATCH', json: { seriesKey } });
        done += 1;
      }
      show({
        tone: 'success',
        text: `${done} ${done === 1 ? 'meeting' : 'meetings'} added to ${title}.`,
      });
      onClose();
    } catch (err) {
      show({
        tone: 'failure',
        text: done
          ? `${done} added, then it stopped: ${(err as Error).message}`
          : (err as Error).message,
      });
    } finally {
      void client.invalidateQueries({ queryKey: ['meetings'] });
      void client.invalidateQueries({ queryKey: ['series'] });
      void client.invalidateQueries({ queryKey: ['meeting'] });
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Add meetings to ${title}`}
      onClose={onClose}
      foot={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={picked.size === 0}
            working={saving ? 'Adding…' : false}
            onClick={() => void add()}
          >
            {picked.size ? `Add ${picked.size}` : 'Add'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <SearchField
          placeholder="Find a meeting by title…"
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
          kbd=""
        />
        {all.isPending ? (
          <SkeletonRows rows={5} />
        ) : candidates.length === 0 ? (
          <Empty lead={q ? 'Nothing matches.' : 'Every meeting is already here.'}>
            {q ? 'Try a different word.' : 'There is nothing left to add to this series.'}
          </Empty>
        ) : (
          <div className="rows scroll-y" style={{ maxHeight: 360 }}>
            {candidates.slice(0, 200).map((m) => (
              <label key={m.id} className="row" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={picked.has(m.id)}
                  onChange={() => toggle(m.id)}
                  aria-label={m.title}
                />
                <div className="row-main">
                  <span className="row-title">{m.title}</span>
                  <span className="row-sub">
                    <span className="mono">{dateLabel(m.startedAt)}</span>
                    {m.seriesKey ? <span className="mono">· moves out of its series</span> : null}
                  </span>
                </div>
              </label>
            ))}
          </div>
        )}
        {candidates.length > 200 ? (
          <p className="t-secondary">Showing the 200 most recent. Type to find an older one.</p>
        ) : null}
      </div>
    </Modal>
  );
}

/** "Wednesdays 11:00" — when the instances agree on a weekday and a clock. Two or more, or nothing. */
function cadenceOf(starts: string[]): string | null {
  if (starts.length < 2) return null;
  const counts = new Map<string, number>();
  for (const iso of starts) {
    const d = new Date(iso);
    const k = `${d.getDay()}|${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const [best, n] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  if (n < 2) return null;
  const [day, time] = best.split('|');
  const days = [
    'Sundays',
    'Mondays',
    'Tuesdays',
    'Wednesdays',
    'Thursdays',
    'Fridays',
    'Saturdays',
  ];
  return `${days[Number(day)]} ${time}`;
}
